import React from "react";
import { MoveFocusInside } from "react-focus-lock";
import { Switch, Link, useHistory, useParams } from "react-router-dom";
import { Toasts } from "@selfdetermine/react-toasts";
import {
  Button,
  Level,
  Columns,
  Icon,
  Card,
} from "@selfdetermine/react-elements";
import { Form } from "@selfdetermine/react-forms";
import {
  JudgeCard,
  useJudge,
  useJudges,
  JudgeForm,
} from "@selfdetermine/react-contactables";
import { RestrictedRoute } from "../RestrictedRoute";
import { Page } from "../Layout";

const CreatePage = () => {
  const history = useHistory();

  return (
    <Page
      title="New Judge"
      breadcrumbs={[
        { title: "Home", to: "/" },
        { title: "Judges", to: "/judges" },
        { title: "New", to: "/judges/new" },
      ]}
    >
      <Page.Section>
        <Columns centered>
          <Columns.Column size="6">
            <MoveFocusInside>
              <JudgeForm
                onDone={() => {
                  Toasts.success("New judge created");
                  history.push("/judges");
                }}
                onCancel={() => history.push("/judges")}
              />
            </MoveFocusInside>
          </Columns.Column>
        </Columns>
      </Page.Section>
    </Page>
  );
};

const EditPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [judge, judgeMeta] = useJudge({ id });

  if (!judge && !judgeMeta.loading) {
    history.push("/judges");
  }

  if (judge.loading) {
    return false;
  }

  return (
    <Page
      title={judge?.name}
      subtitle="Update"
      breadcrumbs={[
        { title: "Home", to: "/" },
        { title: "Judges", to: "/judges" },
        { title: judge?.name, to: `/judges/${judge?.id}` },
        { title: "Update", to: `/judges/${judge?.id}/edit` },
      ]}
    >
      <Page.Section>
        <Columns centered>
          <Columns.Column size="6">
            <MoveFocusInside>
              <JudgeForm
                defaultValues={judge}
                onDone={() => {
                  Toasts.success("Judge updated");
                  history.push(`/judges/${judge?.id}`);
                }}
                onCancel={() => history.push(`/judges/${judge?.id}`)}
              />
            </MoveFocusInside>
          </Columns.Column>
        </Columns>
      </Page.Section>
    </Page>
  );
};

const DetailsPage = () => {
  const history = useHistory();
  const { id } = useParams();
  const [judge, judgeMeta] = useJudge({ id });

  if (!judge && !judgeMeta.loading) {
    history.push("/judges");
  }

  return (
    <Page
      subtitle="Judge Details"
      title={judge?.name}
      breadcrumbs={[
        { title: "Home", to: "/" },
        { title: "Judges", to: "/judges" },
        { title: judge?.name, to: `/judges/${judge?.id}` },
      ]}
    >
      {judge ? (
        <Page.Section>
          <Level mobile>
            <Level.Right>
              <Level.Item>
                <Button as={Link} to={`/judges/${judge.id}/edit`}>
                  <Icon name="pencil" />
                  <span>Edit</span>
                </Button>
              </Level.Item>
            </Level.Right>
          </Level>
        </Page.Section>
      ) : null}
    </Page>
  );
};

const IndexPage = () => {
  const [filters, setFilters] = React.useState({});
  const [judges, judgesMeta] = useJudges({ filters });

  return (
    <Page
      title="Judges"
      breadcrumbs={[
        { title: "Home", to: "/" },
        { title: "Judges", to: "/judges" },
      ]}
    >
      <Page.Section>
        <Level>
          <Level.Right>
            <Button as={Link} to="/judges/new" color="success">
              <Icon name="plus" />
              <span>New Judge</span>
            </Button>
          </Level.Right>
        </Level>
        <Columns>
          <Columns.Column size="3">
            <Card>
              <Card.Header title="Search" />
              <Card.Content>
                <Form
                  disabled
                  onSubmit={setFilters}
                  loading={judgesMeta.loading}
                >
                  <Form.Input type="search" name="name" label="Name" />
                  <Form.Input
                    type="search"
                    name="badgeNumber"
                    label="Badge number"
                  />
                  <Level mobile>
                    <Level.Right>
                      <Level.Item>
                        <Form.SubmitButton>Search</Form.SubmitButton>
                      </Level.Item>
                    </Level.Right>
                  </Level>
                </Form>
              </Card.Content>
            </Card>
          </Columns.Column>
          <Columns.Column>
            {judges.map((judge) => (
              <Link to={`/judges/${judge.id}`} key={judge.id}>
                <JudgeCard judge={judge} />
              </Link>
            ))}
          </Columns.Column>
        </Columns>
      </Page.Section>
    </Page>
  );
};

export const Judges = () => {
  return (
    <Switch>
      <RestrictedRoute
        exact
        path="/judges/new"
        component={CreatePage}
        permission="judge.create"
      />
      <RestrictedRoute
        exact
        path="/judges/:id/edit"
        component={EditPage}
        permission="judge.edit"
      />
      <RestrictedRoute
        exact
        path="/judges/:id"
        component={DetailsPage}
        permission="judge.read"
      />
      <RestrictedRoute
        exact
        path="/judges"
        component={IndexPage}
        permission="judge.index"
      />
    </Switch>
  );
};
